import React, { useEffect, useMemo, useState } from "react";
import { pvDesignApi } from "../../services/pvDesignApi";
import { addToast } from "../../redux/toast";
import { useDispatch, useSelector } from "react-redux";
import {
  addPVRequest,
  setPVDesignError,
  setPVDesignRequestBoxOpen,
  setPVDesignSubmitting,
  setPVRequests,
  setPVResultsForRequest,
  setSelectedRequestId,
  updatePVRequestStatus,
  setSelectedRoofs,
  setIsSelectRoofOpen,
  closePVDesign,
} from "../../redux/pvDesign";
import { reduxLocalStorage } from "../utility/storage";
import { Form, Button } from "react-bootstrap";
import { TbRefresh } from "react-icons/tb";
import {
  ExecutionModal,
  ExecutionNavigation,
  ExecutionContent,
  ExecutionForm,
  ExecutionTable,
  ExecutionActions,
} from "../general/ExecutionModal";
import { setIsPolygonPlacementMode } from "../../redux/polygonPlacement";

const formatDateTime = (d) => {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return dt.toLocaleString();
  } catch {
    return String(d);
  }
};

const NewRequestForm = ({ onSubmit, isSubmitting, params, setParams }) => {
  const { selectedRoofs } = useSelector((state) => state.pvDesign);
  const hasSelectedRoofs = selectedRoofs.length > 0;
  const dispatch = useDispatch();

  const [nameChanged, setNameChanged] = useState(false);

  const isPair = params.layoutType === "pair";
  const pvFacingOptions = isPair
    ? ["EW", "NS"]
    : ["E", "W", "N", "S", "SW", "SE"];

  const submit = async (e) => {
    e.preventDefault();
    if (!params.name) return;
    const payload = {
      ...params,
      tilt: params.tiltIsHorizontal ? 0 : Number(params.tilt || 0),
    };
    await onSubmit?.({ name: params.name, params: payload });
    dispatch(setSelectedRoofs([]));
  };

  useEffect(() => {
    if (!nameChanged && params.name) setNameChanged(true);
  }, [params.name, nameChanged]);

  const pvFacingIsSWOrSE = ["SW", "SE"].includes(params.pvFacing);

  return (
    <ExecutionForm onSubmit={submit}>
      <div className="pv-form-grid">
        <Form.Group className="pv-field" style={{ gridColumn: "span 3" }}>
          <Form.Label className="pv-label" htmlFor="pv-name">
            Name *
          </Form.Label>
          <Form.Control
            id="pv-name"
            type="text"
            className={`pv-input ${
              !params.name && nameChanged ? "pv-input--error" : ""
            }`}
            placeholder="Design name"
            value={params.name}
            onChange={(e) => setParams({ ...params, name: e.target.value })}
          />
        </Form.Group>

        <Form.Group className="pv-field">
          <Form.Label className="pv-label">Layout *</Form.Label>
          <div className="pv-radio-group">
            <Form.Check
              type="radio"
              name="layoutType"
              id="layout-pair"
              label="Pair"
              checked={params.layoutType === "pair"}
              onChange={() =>
                setParams({ ...params, layoutType: "pair", pvFacing: "EW" })
              }
            />
            <Form.Check
              type="radio"
              name="layoutType"
              id="layout-single"
              label="Single"
              checked={params.layoutType === "single"}
              onChange={() =>
                setParams({ ...params, layoutType: "single", pvFacing: "E" })
              }
            />
          </div>
        </Form.Group>

        <Form.Group className="pv-field">
          <Form.Label className="pv-label" htmlFor="pv-facing">
            PV Facing
          </Form.Label>
          <Form.Select
            id="pv-facing"
            className="pv-select"
            value={params.pvFacing}
            onChange={(e) => {
              const newRowsOrientation = ["SW", "SE"].includes(e.target.value)
                ? "pure_geographic"
                : params.rowsOrientation;
              setParams({
                ...params,
                pvFacing: e.target.value,
                rowsOrientation: newRowsOrientation,
              });
            }}
          >
            {pvFacingOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="pv-field">
          <Form.Label className="pv-label">Rows Orientation</Form.Label>
          <div className="pv-radio-group">
            <Form.Check
              type="radio"
              name="rowsOrientation"
              id="rows-shape"
              label="Shape Following"
              checked={params.rowsOrientation === "shape_following"}
              onChange={() =>
                setParams({ ...params, rowsOrientation: "shape_following" })
              }
              disabled={pvFacingIsSWOrSE}
            />
            <Form.Check
              type="radio"
              name="rowsOrientation"
              id="rows-geo"
              label="Pure Geographic"
              checked={params.rowsOrientation === "pure_geographic"}
              onChange={() =>
                setParams({ ...params, rowsOrientation: "pure_geographic" })
              }
              disabled={pvFacingIsSWOrSE}
            />
          </div>
        </Form.Group>
      </div>

      <div className="pv-form-grid-3">
        <Form.Group className="pv-field">
          <Form.Label className="pv-label">Tilt (deg)</Form.Label>
          <Form.Control
            type="number"
            className="pv-input pv-number"
            value={params.tilt}
            onChange={(e) =>
              setParams({ ...params, tilt: Number(e.target.value) })
            }
          />
        </Form.Group>
        <Form.Group className="pv-field">
          <Form.Label className="pv-label" htmlFor="pv-width">
            Module Width (m)
          </Form.Label>
          <Form.Control
            id="pv-width"
            type="number"
            step="0.001"
            className="pv-input pv-number"
            value={params.width}
            onChange={(e) =>
              setParams({ ...params, width: Number(e.target.value) })
            }
          />
        </Form.Group>
        <Form.Group className="pv-field">
          <Form.Label className="pv-label" htmlFor="pv-height">
            Module Height (m)
          </Form.Label>
          <Form.Control
            id="pv-height"
            type="number"
            step="0.001"
            className="pv-input pv-number"
            value={params.height}
            onChange={(e) =>
              setParams({ ...params, height: Number(e.target.value) })
            }
          />
        </Form.Group>
      </div>

      <div className="pv-form-grid-3">
        <Form.Group className="pv-field">
          <Form.Label className="pv-label" htmlFor="pv-min-row">
            Min Offset Between Rows (m)
          </Form.Label>
          <Form.Control
            id="pv-min-row"
            type="number"
            step="0.001"
            className="pv-input pv-number"
            value={params.minRowOffset}
            onChange={(e) =>
              setParams({ ...params, minRowOffset: Number(e.target.value) })
            }
          />
        </Form.Group>
        <Form.Group className="pv-field">
          <Form.Label className="pv-label" htmlFor="pv-min-col">
            Min Offset Between Cols (m)
          </Form.Label>
          <Form.Control
            id="pv-min-col"
            type="number"
            step="0.001"
            className="pv-input pv-number"
            value={params.minColOffset}
            onChange={(e) =>
              setParams({ ...params, minColOffset: Number(e.target.value) })
            }
          />
        </Form.Group>
        {isPair && (
          <Form.Group className="pv-field">
            <Form.Label className="pv-label" htmlFor="pv-gap">
              Gap Within Module Pair (m)
            </Form.Label>
            <Form.Control
              id="pv-gap"
              type="number"
              step="0.001"
              className="pv-input pv-number"
              value={params.gapWithinModule}
              onChange={(e) =>
                setParams({
                  ...params,
                  gapWithinModule: Number(e.target.value),
                })
              }
            />
          </Form.Group>
        )}

        {hasSelectedRoofs && <span>{selectedRoofs.length} roofs selected</span>}
      </div>

      <ExecutionActions>
        <Button
          variant="primary"
          onClick={() => {
            dispatch(setIsSelectRoofOpen(true));
            dispatch(setPVDesignRequestBoxOpen(false));
          }}
        >
          Select Roofs
        </Button>
        {hasSelectedRoofs && (
          <Button
            type="submit"
            disabled={isSubmitting || !params.name}
            variant="success"
            className="pv-ui-submit-btn"
          >
            {isSubmitting
              ? "Executing..."
              : `Execute ${selectedRoofs.length} Roofs`}
          </Button>
        )}
      </ExecutionActions>
    </ExecutionForm>
  );
};

const ConfigOffsetRulesForm = ({
  onSubmit,
  params,
  setParams,
  formParameNames,
}) => {
  const dispatch = useDispatch();
  const submit = async (e) => {
    e.preventDefault();
    await onSubmit?.({ params });
  };

  return (
    <ExecutionForm onSubmit={submit}>
      <div className="pv-form-grid-2">
        {formParameNames.map((param) => (
          <Form.Group className="pv-field">
            <Form.Label className="pv-label">{param.label}</Form.Label>
            <Form.Control
              type="number"
              className="pv-input pv-number"
              value={params[param.name]}
              onChange={(e) =>
                setParams({ ...params, [param.name]: Number(e.target.value) })
              }
            />
          </Form.Group>
        ))}
      </div>

      <div>
        <Button
          variant="primary"
          onClick={() => {
            dispatch(setPVDesignRequestBoxOpen(false));
            dispatch(setIsPolygonPlacementMode(true));
          }}
        >
          Custom Offset Rules
        </Button>
      </div>

      <ExecutionActions>
        <Button type="submit" variant="success" className="pv-ui-submit-btn">
          Submit
        </Button>
      </ExecutionActions>
    </ExecutionForm>
  );
};

const PVDesignManager = () => {
  const dispatch = useDispatch();
  const { requestBox, requests, resultsByRequestId } = useSelector(
    (s) => s.pvDesign
  );
  const projectId = reduxLocalStorage.getItem("project_id");
  const token = localStorage.getItem("token");
  const [activeTab, setActiveTab] = useState("history");

  const columns = useMemo(
    () => [
      { key: "name", label: "Name" },
      {
        key: "createdAt",
        label: "Date",
        render: (r) => formatDateTime(r.createdAt),
      },
      {
        key: "status",
        label: "Status",
        render: (r) => (
          <span
            className={`pv-chip ${
              r.status ? `pv-chip--status-${String(r.status)}` : ""
            }`}
          >
            {r.status || "-"}
          </span>
        ),
      },
      { key: "recordsCount", label: "# of Records" },
    ],
    []
  );

  useEffect(() => {
    if (!requestBox.isOpen) return;
    const load = async () => {
      const rows = await pvDesignApi.listRequests({ projectId, token });
      dispatch(setPVRequests(rows));
    };
    load();
  }, [requestBox.isOpen, dispatch, projectId, token]);

  const submit = async ({ name, params }) => {
    try {
      dispatch(setPVDesignSubmitting(true));
      const created = await pvDesignApi.createRequest({
        projectId,
        token,
        name,
        params,
      });
      dispatch(addPVRequest(created));
      setActiveTab("history");
      dispatch(
        addToast({
          id: Math.random().toString(36).slice(2),
          type: "success",
          message: `Request "${name}" submitted`,
        })
      );
    } catch (e) {
      dispatch(setPVDesignError("Failed to create request"));
      dispatch(
        addToast({
          id: Math.random().toString(36).slice(2),
          type: "error",
          message: `Failed to submit "${name}"`,
        })
      );
    } finally {
      dispatch(setPVDesignSubmitting(false));
    }
  };

  const fetchResults = async (requestId) => {
    const designs = await pvDesignApi.getResults({
      projectId,
      token,
      requestId,
    });
    dispatch(setPVResultsForRequest({ requestId, designs }));
    dispatch(
      updatePVRequestStatus({
        id: requestId,
        status: "done",
        recordsCount: designs.reduce(
          (sum, d) => sum + (d.panels?.length || 0),
          0
        ),
      })
    );
    dispatch(
      addToast({
        id: Math.random().toString(36).slice(2),
        type: "info",
        message: `Results ready for request #${String(requestId).slice(
          0,
          5
        )}...`,
      })
    );
  };

  const tabItems = [
    { key: "history", label: "History" },
    { key: "new", label: "+ New request" },
    { key: "configOffsetRules", label: "Offset Rules" },
  ];

  const [params, setParams] = useState({
    name: "",
    layoutType: "pair",
    pvFacing: "EW",
    rowsOrientation: "shape_following",
    tiltIsHorizontal: false,
    tilt: 10,
    width: 1.8,
    height: 1.15,
    minRowOffset: 0.3,
    minColOffset: 0.018,
    gapWithinModule: 0.078,
  });

  const offsetRulesformParameNames = [
    {
      name: "ledge",
      label: "Ledge (m)",
      value: 0.9,
    },
    {
      name: "entry",
      label: "Entry (m)",
      value: 0.9,
    },
    {
      name: "obstacle",
      label: "Obstacle (m)",
      value: 0.1,
    },
    {
      name: "firewall",
      label: "Firewall (m)",
      value: 0.5,
    },
  ];

  const [offsetRules, setOffsetRules] = useState(
    offsetRulesformParameNames.reduce((acc, param) => {
      acc[param.name] = param.value;
      return acc;
    }, {})
  );

  return (
    <ExecutionModal
      isOpen={requestBox.isOpen}
      onClose={() => dispatch(closePVDesign())}
      title="PV Design"
      size="large"
    >
      <ExecutionNavigation
        items={tabItems}
        activeItem={activeTab}
        onItemClick={setActiveTab}
      />

      {activeTab === "history" && (
        <ExecutionContent className="overflow-auto" style={{ height: "65vh" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <h4 className="pv-modal-title">History</h4>
            <button
              onClick={() => requests[0]?.id && fetchResults(requests[0].id)}
              disabled={!requests.length}
              className="pv-ui-btn"
            >
              <TbRefresh />
              Refresh
            </button>
          </div>

          <ExecutionTable
            columns={columns}
            data={requests}
            renderActions={(row) => (
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={async () => {
                    if (!resultsByRequestId[row.id]?.length)
                      await fetchResults(row.id);
                    dispatch(setSelectedRequestId(row.id));
                    dispatch(setPVDesignRequestBoxOpen(false));
                  }}
                  className="pv-ui-primary"
                >
                  View
                </button>
              </div>
            )}
            emptyMessage={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <p>No requests yet. Create a new request to get started.</p>
                <button
                  onClick={() => setActiveTab("new")}
                  className="pv-ui-primary"
                >
                  Create new request
                </button>
              </div>
            }
          />
        </ExecutionContent>
      )}

      {activeTab === "new" && (
        <ExecutionContent
          className="pv-modal-content"
          style={{ height: "65vh" }}
        >
          <h4 className="pv-modal-title">New request</h4>
          <NewRequestForm
            onSubmit={submit}
            isSubmitting={requestBox.isSubmitting}
            params={params}
            setParams={setParams}
          />
        </ExecutionContent>
      )}
      {activeTab === "configOffsetRules" && (
        <ExecutionContent>
          <h4 className="pv-modal-title">Config Offset Rules</h4>
          <ConfigOffsetRulesForm
            onSubmit={() => {
              setActiveTab("new");
            }}
            params={offsetRules}
            setParams={setOffsetRules}
            formParameNames={offsetRulesformParameNames}
          />
        </ExecutionContent>
      )}
    </ExecutionModal>
  );
};

export default PVDesignManager;


